import { classifyTrainType } from './trainUtils';

// Thresholds and definitions are taken from railapi_daily_operations_report.py so that the
// dashboard and the daily operations mail can never disagree on a number.

// DB punctuality definition: a delay below 6 minutes counts as on time
export const DELAY_THRESHOLD_MIN = 6;
// "Hochverspätung" in the report
export const HIGH_DELAY_THRESHOLD_MIN = 60;
// Below this sample size the report refuses to judge a route or segment
export const MIN_TRAINS_FOR_VERDICT = 3;
// The operating day runs 04:00 → 04:00 the next morning (Europe/Berlin)
export const OPERATING_DAY_START_HOUR = 4;

// Traffic-light thresholds for on-time percentage, as used in the report's tables
export const PUNCTUALITY_GOOD_PCT = 80;
export const PUNCTUALITY_FAIR_PCT = 65;

/**
 * Colour for a single delay in minutes. One scale for the whole app: the dots on the map,
 * the stops in the route and the delay chips all have to mean the same thing.
 */
export const delayColor = (delayMin, status) => {
  if (status === 'cancelled') return '#8b949e';
  const delay = delayMin || 0;
  if (delay >= 15) return '#f85149';
  if (delay >= DELAY_THRESHOLD_MIN) return '#f78166';
  if (delay >= 3) return '#f0b429';
  return '#3fb950';
};

export const getDelay = (scheduled, actual) => {
  if (!scheduled || !actual) return null;
  const diff = Math.round((new Date(actual) - new Date(scheduled)) / 60000);
  return diff > 0 ? diff : null;
};

export const isDelayed = (scheduled, actual) => {
  const delay = getDelay(scheduled, actual);
  return delay !== null && delay >= DELAY_THRESHOLD_MIN;
};

// routeIdx is unreliable / often all 0 — sort stations by their earliest known time
export const getStationSortTime = (s) => {
  const times = [s.departureTime_scheduled, s.arrivalTime_scheduled]
    .filter(Boolean)
    .map(t => new Date(t).getTime());
  return times.length > 0 ? Math.min(...times) : 0;
};

export const sortStations = (stations) =>
  [...stations].sort((a, b) => getStationSortTime(a) - getStationSortTime(b));

// Worst delay seen at a station (arrival or departure)
export const getStationDelay = (station) => {
  const arrDelay = getDelay(station.arrivalTime_scheduled, station.arrivalTime_actual);
  const depDelay = getDelay(station.departureTime_scheduled, station.departureTime_actual);
  return Math.max(arrDelay || 0, depDelay || 0);
};

// Course angle in degrees (0 = north, clockwise) between two [lat, lng] points
export const bearingBetween = (from, to) => {
  if (!from || !to) return 0;
  const toRad = (d) => (d * Math.PI) / 180;
  const φ1 = toRad(from[0]);
  const φ2 = toRad(to[0]);
  const Δλ = toRad(to[1] - from[1]);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const deg = (Math.atan2(y, x) * 180) / Math.PI;
  return (deg + 360) % 360;
};

// Aggregation group used by the dashboard aggregates (daily_aggregates_*.train_group)
export const trainGroupOf = (train) => {
  switch (classifyTrainType(train)) {
    case 'fern': return 'fern';
    case 'sbahn': return 'sbahn';
    case 'regio': return 'nah';
    default: return 'sonstige';
  }
};

export const trainGroupLabels = {
  fern: 'Fernverkehr',
  nah: 'Nahverkehr',
  sbahn: 'S-Bahn',
  sonstige: 'Sonstige',
};

/** Key of the track section between two stops, the way /geo/segments hands them out */
export const segmentKey = (train) => {
  const from = train?.prev_station?.id;
  const to = train?.next_station?.id;
  return from != null && to != null ? `${from}-${to}` : null;
};

/**
 * Where along a section's geometry a train stands at fraction f of the way.
 *
 * Walked by distance, not by point index. The points are spaced unevenly — dense through
 * curves, sparse on straight track — so stepping through them one by one would make trains
 * crawl around every bend and race down the straights.
 */
export const walkSegment = (geometry, f) => {
  if (!geometry || geometry.length < 2) return null;

  let total = 0;
  const spans = [];
  for (let i = 1; i < geometry.length; i++) {
    const dLat = geometry[i][0] - geometry[i - 1][0];
    // A degree of longitude is shorter than a degree of latitude; without the correction an
    // east-west stretch would count for about a third less than it should in Germany
    const dLng = (geometry[i][1] - geometry[i - 1][1]) * Math.cos((geometry[i][0] * Math.PI) / 180);
    total += Math.hypot(dLat, dLng);
    spans.push(total);
  }
  if (total <= 0) return null;

  const target = total * Math.max(0, Math.min(1, f));
  for (let i = 0; i < spans.length; i++) {
    if (spans[i] >= target) {
      const before = i === 0 ? 0 : spans[i - 1];
      const share = spans[i] === before ? 0 : (target - before) / (spans[i] - before);
      const p = geometry[i];
      const q = geometry[i + 1];
      return {
        lat: p[0] + (q[0] - p[0]) * share,
        lng: p[1] + (q[1] - p[1]) * share,
        bearing: bearingBetween(p, q),
      };
    }
  }
  const last = geometry[geometry.length - 1];
  return { lat: last[0], lng: last[1], bearing: null };
};

/**
 * Where a train from the map snapshot stands at a given instant.
 *
 * The snapshot carries the previous and the next stop with their times, so the position for
 * *any* moment can be worked out locally — that is what keeps the trains moving between two
 * polls instead of trailing a full interval behind.
 *
 * @param segments Map of section key -> [[lat, lng], ...]. Where a section is known the
 *                 train follows the rails; where it is not it takes the straight line, which
 *                 is honest about what is known rather than inventing track.
 */
export const positionAt = (train, nowMs, segments) => {
  const a = train?.prev_station;
  const b = train?.next_station;
  if (!a || !a.time) return null;
  // No stop after this one - the API ran out of timetable, not the train out of track. It
  // sends the last place the train was reported and says so via position_source; drawing it
  // there beats dropping it off the map, which is what used to happen to every train whose
  // stops come from station polling once it passed the last polled station.
  if (!b || !b.time) {
    return Number.isFinite(a.lat) && Number.isFinite(a.lng)
      ? { lat: a.lat, lng: a.lng, bearing: null }
      : null;
  }
  const t0 = Date.parse(a.time);
  const t1 = Date.parse(b.time);
  if (!isFinite(t0) || !isFinite(t1) || t1 <= t0) return null;
  const f = Math.max(0, Math.min(1, (nowMs - t0) / (t1 - t0)));

  const geometry = segments?.get(segmentKey(train));
  if (geometry) {
    const point = walkSegment(geometry, f);
    if (point) return point;
  }

  return {
    lat: a.lat + (b.lat - a.lat) * f,
    lng: a.lng + (b.lng - a.lng) * f,
    bearing: null,
  };
};

/**
 * Estimate where a train is at a given time by interpolating along its route.
 *
 * @param stationsWithCoords chronologically sorted journey stations that have lat/lng
 * @param routeCoords        optional [[lat, lng], ...] route geometry; falls back to
 *                           straight lines between stations when empty
 * @param now                Date to evaluate
 * @returns { pos, delayed, bearing, progress, fromStation, toStation } or null when the
 *          train is outside its journey window (± 60 min) or has too few coordinates
 */
export const interpolatePosition = (stationsWithCoords, routeCoords, now) => {
  if (!stationsWithCoords || stationsWithCoords.length < 2) return null;
  const coords = routeCoords || [];
  const sixtyMin = 60 * 60 * 1000;

  // Journey time window: first departure to last arrival
  const first = stationsWithCoords[0];
  const last = stationsWithCoords[stationsWithCoords.length - 1];
  const journeyStart = new Date(first.departureTime_actual || first.departureTime_scheduled);
  const journeyEnd = new Date(last.arrivalTime_actual || last.arrivalTime_scheduled);

  // Only show position if within 60 min before start or 60 min after end
  if (now < journeyStart - sixtyMin || now > journeyEnd.getTime() + sixtyMin) return null;

  const second = stationsWithCoords[1];
  const secondLast = stationsWithCoords[stationsWithCoords.length - 2];

  // If before first departure, show at first station
  if (now < journeyStart) {
    return {
      pos: [first.lat, first.lng],
      delayed: isDelayed(first.departureTime_scheduled, first.departureTime_actual),
      bearing: bearingBetween([first.lat, first.lng], [second.lat, second.lng]),
      progress: 0,
      fromStation: first,
      toStation: second,
    };
  }
  // If after last arrival, show at last station
  if (now > journeyEnd) {
    return {
      pos: [last.lat, last.lng],
      delayed: isDelayed(last.arrivalTime_scheduled, last.arrivalTime_actual),
      bearing: bearingBetween([secondLast.lat, secondLast.lng], [last.lat, last.lng]),
      progress: 1,
      fromStation: secondLast,
      toStation: last,
    };
  }

  for (let i = 0; i < stationsWithCoords.length - 1; i++) {
    const from = stationsWithCoords[i];
    const to = stationsWithCoords[i + 1];
    const fromTime = new Date(from.departureTime_actual || from.departureTime_scheduled);
    const toTime = new Date(to.arrivalTime_actual || to.arrivalTime_scheduled);

    if (!fromTime || !toTime || fromTime >= toTime) continue;
    if (now < fromTime || now > toTime) continue;

    const progress = (now - fromTime) / (toTime - fromTime);
    const segDelayed =
      getStationDelay(from) >= DELAY_THRESHOLD_MIN || getStationDelay(to) >= DELAY_THRESHOLD_MIN;

    const makeResult = (pos, bearing) => ({
      pos,
      delayed: segDelayed,
      bearing,
      progress,
      fromStation: from,
      toStation: to,
    });

    // Interpolate along polyline if available
    if (coords.length >= 2) {
      const findNearest = (lat, lng) => {
        let minDist = Infinity, minIdx = 0;
        for (let j = 0; j < coords.length; j++) {
          const d = (coords[j][0] - lat) ** 2 + (coords[j][1] - lng) ** 2;
          if (d < minDist) { minDist = d; minIdx = j; }
        }
        return minIdx;
      };
      const fromIdx = findNearest(from.lat, from.lng);
      const toIdx = findNearest(to.lat, to.lng);
      if (fromIdx < toIdx) {
        const seg = coords.slice(fromIdx, toIdx + 1);
        const dists = [0];
        for (let j = 1; j < seg.length; j++) {
          const dl = seg[j][0] - seg[j - 1][0], dn = seg[j][1] - seg[j - 1][1];
          dists.push(dists[j - 1] + Math.sqrt(dl * dl + dn * dn));
        }
        const target = dists[dists.length - 1] * progress;
        for (let j = 1; j < dists.length; j++) {
          if (dists[j] >= target) {
            const p = (target - dists[j - 1]) / (dists[j] - dists[j - 1]);
            return makeResult(
              [
                seg[j - 1][0] + (seg[j][0] - seg[j - 1][0]) * p,
                seg[j - 1][1] + (seg[j][1] - seg[j - 1][1]) * p,
              ],
              bearingBetween(seg[j - 1], seg[j])
            );
          }
        }
      }
    }

    // Fallback: linear between stations
    return makeResult(
      [from.lat + (to.lat - from.lat) * progress, from.lng + (to.lng - from.lng) * progress],
      bearingBetween([from.lat, from.lng], [to.lat, to.lng])
    );
  }
  return null;
};
